import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { SEOHead } from "@/features/shared/SEOHead";
import { generateProfessionalServiceSchema } from "@/lib/business-schema";
import { useRevealObserver } from "@/hooks/useRevealObserver";
import { MissedCallCalculator } from "@/components/shared/MissedCallCalculator";
import { ServiceFAQ } from "@/components/shared/ServiceFAQ";
import { Check, ArrowRight, Minus } from "lucide-react";

const Pricing = () => {
  useRevealObserver();
  const [selectedQualifier, setSelectedQualifier] = useState<number | null>(null);
  const [annual, setAnnual] = useState(false);

  // Public entry SKUs (the only hard prices on the site, per the pricing doctrine).
  const entries = [
    {
      name: "7-Day Website Launch",
      build: "from $997",
      buildLabel: "Required Build & Onboarding",
      monthly: "$189/mo",
      monthlyLabel: "the Frayze Core plan",
      desc: "A fast, lead-capturing website live in 7 days, with a real CRM behind it.",
      bullets: [
        "5-page conversion-focused site",
        "CRM core with lead capture wired in",
        "Live in 7 days, no contracts",
      ],
      cta: "See the Launch Program",
      href: "/website-launch-program/",
    },
    {
      name: "Frayze Reception",
      build: "$500",
      buildLabel: "Required Build & Onboarding",
      monthly: "$197/mo",
      monthlyLabel: "managed AI reception, cancel anytime",
      desc: "A managed AI receptionist that answers every call 24/7, books the appointment, and logs the caller.",
      bullets: [
        "Every call answered, day or night",
        "Bookings straight into your calendar",
        "We build it, train it, and keep tuning it",
      ],
      cta: "See Frayze Reception",
      href: "/platform/ai-receptionist/",
    },
  ];

  const qualifiers = [
    { problem: "The phone rings and nobody picks it up.", rec: "Frayze Reception · $197/mo", href: "/platform/ai-receptionist/", ctaLabel: "See Frayze Reception" },
    { problem: "I don't have a professional website, or mine is embarrassing.", rec: "7-Day Website Launch + Frayze Core", href: "/website-launch-program/", ctaLabel: "See the Launch Program" },
    { problem: "I'm getting leads but losing them to slow follow-up.", rec: "Frayze Managed · $399/mo", href: "/book-consultation/", ctaLabel: "Book Strategy Session" },
    { problem: "Follow-up is solid, but new leads aren't consistent.", rec: "Frayze Growth · $699/mo", href: "/book-consultation/", ctaLabel: "Book Strategy Session" },
    { problem: "High lead volume and ready for a full growth operation.", rec: "Frayze Scale · $1,199/mo", href: "/book-consultation/", ctaLabel: "Book Strategy Session" },
    { problem: "We're a dealer group or multi-location operation.", rec: "Enterprise · Dealer Groups & Multi-Location", href: "/automotive/gta-dealers/", ctaLabel: "Talk to Denis" },
  ];

  // Plans mirror the live GHL Products exactly (names, monthlies, yearly = ~2 months
  // free, included usage credits, feature lists). Change GHL first, then here.
  const plans = [
    {
      id: 1,
      title: "Frayze Core",
      label: "You run it",
      monthly: 189,
      yearly: 1899,
      credits: null,
      description: "The foundation: your website and CRM on one platform, wired so no lead slips through. You run it day to day; we keep the platform solid.",
      features: [
        "Professional website, hosted and maintained",
        "CRM with unified inbox (SMS, email, FB/IG, Google)",
        "Missed-call text-back",
        "Speed-to-lead instant reply on every new lead",
        "Sales pipeline and appointment calendar",
        "Review request sending + mobile app",
      ],
      cta: "Start with the Launch Program",
      ctaHref: "/website-launch-program/",
    },
    {
      id: 2,
      title: "Frayze Managed",
      label: "We run it",
      monthly: 399,
      yearly: 3999,
      credits: 10,
      description: "The same system with our team operating it. The platform stops being something you have to remember to use.",
      features: [
        "Everything in Core, plus",
        "We manage and tune your follow-up sequences",
        "Automated review engine (request, monitor, respond)",
        "Social post scheduling to your pages",
        "Monthly website edits and performance report",
        "Priority support",
      ],
      cta: "Book Strategy Session",
      ctaHref: "/book-consultation/",
      recommended: true,
    },
    {
      id: 3,
      title: "Frayze Growth",
      label: "New revenue",
      monthly: 699,
      yearly: 6999,
      credits: 25,
      description: "Adds the systems that create new revenue instead of just catching what already comes in.",
      features: [
        "Everything in Managed, plus",
        "AI website chat, capturing leads 24/7",
        "Database reactivation campaigns (email + SMS)",
        "Call tracking and lead-source attribution",
        "Quarterly SEO tune-up",
        "Monthly strategy call",
      ],
      cta: "Book Strategy Session",
      ctaHref: "/book-consultation/",
    },
    {
      id: 4,
      title: "Frayze Scale",
      label: "Full growth op",
      monthly: 1199,
      yearly: 11999,
      credits: 50,
      description: "For businesses where lead volume justifies a full outsourced growth operation.",
      features: [
        "Everything in Growth, plus",
        "AI voice agent for after-hours and overflow calls",
        "Multi-location, per-location reporting",
        "Full campaign management across all channels",
        "Custom integrations and dedicated account manager",
        "Weekly reporting and review",
      ],
      cta: "Book Strategy Session",
      ctaHref: "/book-consultation/",
    },
  ];

  // A-la-carte add-ons, from the live GHL Add-Ons product. Usage fees extra.
  const addons = [
    { name: "Listing Management", price: "$49/mo" },
    { name: "Dedicated Email IP", price: "$80/mo" },
    { name: "AI B2B Prospecting", price: "$49/mo" },
    { name: "WordPress Hosting", price: "$21/mo" },
    { name: "Extra Phone Line", price: "$2/mo" },
  ];

  const comparisonRows = [
    { label: "Live in",         freelancer: "4–8 weeks",      agency: "6–12 weeks",         frayze: "7 days" },
    { label: "Monthly cost",    freelancer: "Project-based",   agency: "$1,500–$5,000+",     frayze: "Scoped to your business" },
    { label: "Missed-call automation", freelancer: false,     agency: false,                 frayze: true },
    { label: "CRM included",    freelancer: false,             agency: false,                 frayze: true },
    { label: "Contract",        freelancer: "Per project",     agency: "12-month minimum",   frayze: "None" },
    { label: "You own it",      freelancer: true,              agency: false,                 frayze: true },
    { label: "Built by an operator who ran it", freelancer: false, agency: "Rarely",         frayze: true },
  ];

  const breadcrumbSchema = {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://frayze.ca/" },
      { "@type": "ListItem", "position": 2, "name": "Pricing", "item": "https://frayze.ca/pricing/" },
    ],
  };

  const productSchema = {
    "@type": "Product",
    "name": "Frayze Growth System",
    "description": "Website, CRM, and lead automation systems for lead-driven businesses: auto dealers, trades, and local service operators. Public entry pricing, managed tiers scoped, no contracts. Built in Thunder Bay.",
    "brand": { "@type": "Brand", "name": "Frayze" },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "CAD",
      "lowPrice": "189.00",
      "highPrice": "1199.00",
      "offerCount": 6,
      "availability": "https://schema.org/InStock",
    },
  };

  return (
    <div className="min-h-screen bg-[#FBFAF7] text-[#101319] font-sans antialiased overflow-x-hidden relative selection:bg-[#2F5FE8] selection:text-white">
      <Helmet>
        {/* Fonts self-hosted globally (index.css) */}
        <style>{`
          @keyframes fadeUp {
            from { opacity: 0; transform: translateY(26px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeUp {
            animation: fadeUp 0.7s cubic-bezier(0.2,0.8,0.2,1) both;
          }
          @keyframes wave {
            0%, 100% { transform: scaleY(0.35); }
            50% { transform: scaleY(1); }
          }
          .animate-wave { animation: wave 1.3s ease-in-out infinite; }
          @keyframes orbDriftA {
            0%, 100% { transform: translate(0, 0) scale(1); }
            33% { transform: translate(48px, -36px) scale(1.06); }
            66% { transform: translate(-24px, 28px) scale(0.97); }
          }
          @keyframes orbDriftB {
            0%, 100% { transform: translate(0, 0) scale(1); }
            40% { transform: translate(-52px, -30px) scale(1.05); }
            75% { transform: translate(30px, 24px) scale(0.96); }
          }
          .animate-orbA { animation: orbDriftA 26s ease-in-out infinite; }
          .animate-orbB { animation: orbDriftB 34s ease-in-out infinite; }
          @media (prefers-reduced-motion: reduce) {
            .animate-fadeUp, .animate-wave, .animate-orbA, .animate-orbB {
              animation: none;
            }
          }
        `}</style>
      </Helmet>

      <SEOHead
        title="Pricing | Website, CRM & AI Systems scoped to your business | Frayze"
        description="Honest pricing for lead-driven businesses: auto dealers, trades & local service. Start with the 7-Day Website Launch from $997 or Frayze Reception from $197/mo; managed systems scoped to your business. No contracts."
        keywords="automotive CRM pricing, contractor website pricing, CRM pricing, Thunder Bay website pricing, growth system pricing, Frayze pricing"
        canonical="https://frayze.ca/pricing/"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            generateProfessionalServiceSchema(),
            breadcrumbSchema,
            productSchema,
          ],
        }}
      />

      <Header />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-[#101319] py-32 md:py-40 flex items-center">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 -left-40 w-[42rem] h-[42rem] rounded-full bg-[radial-gradient(circle,rgba(47,95,232,0.16),transparent_65%)] blur-3xl animate-orbA" />
          <div className="absolute -bottom-48 right-[-12%] w-[38rem] h-[38rem] rounded-full bg-[radial-gradient(circle,rgba(19,167,184,0.10),transparent_65%)] blur-3xl animate-orbB" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:56px_56px] [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />
        </div>
        <div className="relative z-10 w-full max-w-[1312px] mx-auto px-6 flex flex-col gap-8 font-instrument">
          <div className="inline-flex items-center gap-2.5 font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#2F5FE8] animate-fadeUp">
            <span className="w-6 h-[1px] bg-[#2F5FE8]"></span>
            Pricing
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-[68px] leading-[1.02] tracking-[-0.04em] font-semibold text-white text-pretty animate-fadeUp">
            Pick your starting point. Be live in 7 days.
          </h1>

          <p className="text-lg md:text-xl leading-relaxed text-[#8A8FA0] max-w-2xl text-pretty animate-fadeUp">
            No contracts on any plan. Managed for you. Usage billed like a phone plan.
          </p>

          <div className="flex flex-wrap items-center gap-3 animate-fadeUp">
            {["Live in 7 days", "No contracts", "Managed for you", "Scale only when ready"].map((chip) => (
              <span key={chip} className="inline-flex items-center gap-2 border border-white/15 bg-white/5 rounded-full px-4 py-2 text-sm font-semibold text-[#C7CBD4]">
                <Check className="w-4 h-4 text-[#1B9E6B]" />
                {chip}
              </span>
            ))}
            <a
              href="#step-1"
              className="inline-flex items-center gap-2 bg-[#2F5FE8] hover:bg-[#2049BE] text-white font-bold rounded-full px-5 py-2 text-sm transition-all hover:scale-[1.02]"
            >
              See the plans
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ============ START TODAY: public entry SKUs ============ */}
      <section id="step-1" className="scroll-mt-20 bg-[#FBFAF7] py-16 md:py-24 font-instrument">
        <div className="max-w-[1312px] mx-auto px-6">
          <div className="max-w-2xl mb-10">
            <div className="reveal font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#2F5FE8] mb-3">
              Step 1 · Start here
            </div>
            <h2 className="reveal reveal-delay-1 text-3xl md:text-4xl font-bold text-[#101319] mb-3">
              Two doors in, priced up front.
            </h2>
            <p className="reveal reveal-delay-2 text-base md:text-lg text-[#5A6270]">
              Fix the website or fix the phones. Either one stands on its own and
              plugs into any plan below whenever you're ready.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {entries.map((e, i) => (
              <div
                key={e.name}
                className={`reveal ${i === 1 ? "reveal-delay-1" : ""} relative overflow-hidden bg-white border border-[#E9E6DE] hover:border-[#2F5FE8]/40 rounded-2xl p-8 md:p-10 flex flex-col gap-6 lift-hover transition-all`}
              >
                <span aria-hidden className="absolute -top-4 right-6 font-mono-ibm text-[96px] leading-none font-medium text-[#101319]/[0.04] select-none">
                  01
                </span>
                <div>
                  <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#2F5FE8] mb-2.5">
                    {i === 0 ? "Fix the website" : "Fix the phones"}
                  </div>
                  <h3 className="text-2xl font-bold text-[#101319] mb-1.5">{e.name}</h3>
                  <p className="text-sm md:text-base text-[#5A6270]">{e.desc}</p>
                </div>
                {i === 1 && (
                  <div className="flex items-center gap-1.5 h-9 max-w-[220px] select-none" aria-hidden>
                    {["h-[40%]", "h-[70%]", "h-full", "h-[60%]", "h-[85%]", "h-[45%]", "h-[75%]", "h-[55%]"].map((h, j) => (
                      <span key={j} className={`flex-1 ${h} bg-[#7E22CE] ${j % 3 === 0 ? "" : j % 3 === 1 ? "opacity-70" : "opacity-40"} rounded-full animate-wave animation-delay-${[200, 300, 400, 500, 600, 200, 300, 400][j]}`} />
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#F4F2EC] border border-[#E9E6DE] rounded-xl p-4">
                    <div className="text-2xl font-bold text-[#101319]">{e.build}</div>
                    <div className="font-mono-ibm text-[10px] tracking-[0.1em] uppercase text-[#5A6270] mt-1">{e.buildLabel}</div>
                  </div>
                  <div className="bg-[#101319] rounded-xl p-4">
                    <div className="text-2xl font-bold text-white">{e.monthly}</div>
                    <div className="font-mono-ibm text-[10px] tracking-[0.1em] uppercase text-[#9BA3B2] mt-1">{e.monthlyLabel}</div>
                  </div>
                </div>
                <ul className="space-y-2.5">
                  {e.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm md:text-base">
                      <Check className="w-5 h-5 text-[#1B9E6B] shrink-0 mt-0.5" />
                      <span className="text-[#101319]">{b}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={e.href}
                  className="mt-auto text-center bg-[#2F5FE8] hover:bg-[#2049BE] text-white font-bold px-6 py-3.5 rounded-xl transition-all hover:scale-[1.02]"
                >
                  {e.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ QUALIFIER ============ */}
      <section className="bg-[#FBFAF7] py-16 md:py-24 border-t border-[#E9E6DE] font-instrument">
        <div className="max-w-[1312px] mx-auto px-6">
          <div className="bg-white border border-[#E9E6DE] rounded-2xl p-8 md:p-12">
            <div className="font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#2F5FE8] text-center mb-3">
              Not sure which step?
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-center text-[#101319] mb-8">
              What's your biggest problem right now?
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8 max-w-2xl mx-auto">
              {qualifiers.map((q, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedQualifier(idx)}
                  className={`text-left px-4 py-3 rounded-xl border-2 transition-all font-medium text-sm md:text-base ${
                    selectedQualifier === idx
                      ? "bg-[#2F5FE8] text-white border-[#2F5FE8]"
                      : "bg-[#F4F2EC] text-[#101319] border-[#E9E6DE] hover:border-[#2F5FE8]"
                  }`}
                >
                  {q.problem}
                </button>
              ))}
            </div>

            {selectedQualifier !== null && (
              <div className="bg-[#F4F2EC] border border-[#E9E6DE] rounded-xl p-6 text-center">
                <div className="font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#2F5FE8] mb-2">
                  Recommended for you
                </div>
                <p className="text-[#101319] font-bold mb-4">
                  {qualifiers[selectedQualifier].rec}
                </p>
                <Link
                  to={qualifiers[selectedQualifier].href}
                  className="inline-flex items-center gap-2 bg-[#2F5FE8] hover:bg-[#2049BE] text-white font-bold px-6 py-3 rounded-xl transition-all hover:scale-[1.02]"
                >
                  {qualifiers[selectedQualifier].ctaLabel}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ============ PLANS (GHL billing truth) ============ */}
      <section className="bg-white py-16 md:py-28 font-instrument">
        <div className="max-w-[1312px] mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-10">
            <div className="text-left">
              <div className="reveal font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#2F5FE8] mb-2">
                The plans
              </div>
              <h2 className="reveal reveal-delay-1 text-3xl md:text-4xl font-bold text-[#101319]">
                One system. Four levels of managed.
              </h2>
            </div>
            {/* Billing toggle */}
            <div className="reveal reveal-delay-1 inline-flex items-center gap-1 bg-[#F4F2EC] border border-[#E9E6DE] rounded-full p-1">
              <button
                onClick={() => setAnnual(false)}
                className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${!annual ? "bg-white text-[#101319] shadow-sm" : "text-[#5A6270]"}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setAnnual(true)}
                className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${annual ? "bg-white text-[#101319] shadow-sm" : "text-[#5A6270]"}`}
              >
                Yearly
                <span className="ml-2 font-mono-ibm text-[10px] tracking-[0.08em] uppercase text-[#1B9E6B]">2 months free</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {plans.map((plan, i) => (
              <div
                key={plan.id}
                className={`reveal ${["", "reveal-delay-1", "reveal-delay-2", "reveal-delay-3"][i % 4]} relative rounded-2xl border transition-all ${
                  plan.recommended
                    ? "border-[#2F5FE8] shadow-lg shadow-[#2F5FE8]/10 ring-1 ring-[#2F5FE8]/20"
                    : "border-[#E9E6DE]"
                } bg-white p-6 md:p-8 flex flex-col lift-hover`}
              >
                {plan.recommended && (
                  <div className="absolute -top-3 left-6 bg-[#2F5FE8] text-white text-[11px] font-mono-ibm font-bold px-3 py-1 rounded-full tracking-[0.12em] uppercase">
                    Most popular
                  </div>
                )}

                <span aria-hidden className="absolute top-2 right-5 font-mono-ibm text-[80px] leading-none font-medium text-[#101319]/[0.04] select-none">
                  {String(plan.id).padStart(2, "0")}
                </span>

                <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#2F5FE8] mb-2">
                  {plan.label}
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-[#101319] mb-4">
                  {plan.title}
                </h3>

                <div className="mb-1.5">
                  <span className="text-4xl font-bold tracking-tight text-[#101319]">
                    ${annual ? plan.yearly.toLocaleString("en-CA") : plan.monthly}
                  </span>
                  <span className="text-sm font-semibold text-[#5A6270]">/{annual ? "yr" : "mo"}</span>
                </div>
                <p className="text-xs text-[#5A6270] mb-5">
                  + Required Build &amp; Onboarding, scoped with a fixed quote
                </p>

                <p className="text-sm text-[#5A6270] leading-relaxed mb-6 flex-grow">
                  {plan.description}
                </p>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5 text-sm">
                      <Check className="w-5 h-5 text-[#1B9E6B] shrink-0 mt-0.5" />
                      <span className="text-[#101319]">{feature}</span>
                    </li>
                  ))}
                </ul>

                {plan.credits && (
                  <div className="mb-6 bg-[#F4F2EC] border border-[#E9E6DE] rounded-lg px-3.5 py-2.5 text-xs font-semibold text-[#101319]">
                    ${plan.credits}/mo usage credits included
                  </div>
                )}

                <Link
                  to={plan.ctaHref}
                  className={`mt-auto text-center px-6 py-3 rounded-xl font-bold text-sm transition-all ${
                    plan.recommended
                      ? "bg-[#2F5FE8] text-white hover:bg-[#2049BE]"
                      : "bg-[#F4F2EC] text-[#101319] hover:bg-[#E9E6DE]"
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}

          </div>

          {/* Enterprise / Groups: full-width dark band, Autocorp pattern */}
          <div className="reveal relative rounded-2xl border border-[#101319] bg-[#101319] text-white p-8 md:p-10 overflow-hidden mb-8">
            <div aria-hidden className="pointer-events-none absolute inset-0">
              <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[radial-gradient(circle,rgba(47,95,232,0.25),transparent_65%)] blur-2xl" />
            </div>
            <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-center">
              <div className="text-left">
                <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#7C9EFF] mb-2">
                  Enterprise · Groups
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-2">Dealer Groups & Multi-Location</h3>
                <p className="text-sm md:text-base text-[#9BA3B2] leading-relaxed mb-5 max-w-2xl">
                  A custom build for dealer groups and multi-location operators. Per-rooftop systems, one rollout, one operator relationship. Scoped with your leadership team.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
                  {[
                    "Everything in Scale, plus",
                    "Per-rooftop CRM and phone systems",
                    "Group-level reporting and attribution",
                    "Dedicated rollout and training",
                    "Built by an operator who ran six rooftops",
                  ].map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5 text-sm">
                      <Check className="w-5 h-5 text-[#1B9E6B] shrink-0 mt-0.5" />
                      <span className="text-[#F2F3F5]">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Link
                to="/automotive/gta-dealers/"
                className="text-center px-8 py-4 rounded-xl font-bold text-sm transition-all bg-white text-[#101319] hover:bg-[#E9E6DE] shrink-0"
              >
                Talk to Denis
              </Link>
            </div>
          </div>

          {/* Add-ons strip: from the live GHL Add-Ons product */}
          <div className="reveal reveal-delay-1 bg-[#F4F2EC] border border-[#E9E6DE] rounded-2xl p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
              <div className="shrink-0">
                <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#2F5FE8] mb-1">
                  Add-ons
                </div>
                <p className="text-sm font-bold text-[#101319]">A la carte, on any plan</p>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {addons.map((a) => (
                  <span key={a.name} className="inline-flex items-center gap-2 bg-white border border-[#E9E6DE] rounded-full px-4 py-2 text-sm">
                    <span className="font-semibold text-[#101319]">{a.name}</span>
                    <span className="font-mono-ibm text-xs text-[#2F5FE8] font-bold">{a.price}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link to="/case-studies/" className="inline-flex items-center gap-1 text-[#2F5FE8] font-bold text-sm hover:underline">
              See real builds <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ COMPARE THE STEPS (feature matrix) ============ */}
      <section className="bg-[#FBFAF7] py-16 md:py-24 border-t border-[#E9E6DE] font-instrument">
        <div className="max-w-[1312px] mx-auto px-6">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <div className="reveal font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#2F5FE8] mb-3">
              Compare the plans
            </div>
            <h2 className="reveal reveal-delay-1 text-3xl md:text-4xl font-bold text-[#101319] mb-4">
              Every plan includes the one before it.
            </h2>
          </div>

          <div className="reveal reveal-delay-1 bg-white border border-[#E9E6DE] rounded-2xl overflow-hidden overflow-x-auto">
            <div className="min-w-[720px]">
              <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] bg-[#F4F2EC] border-b border-[#E9E6DE]">
                <div className="p-4" />
                {["Core", "Managed", "Growth", "Scale"].map((t, i) => (
                  <div key={t} className="p-4 text-center border-l border-[#E9E6DE]">
                    <p className="font-mono-ibm text-[10px] tracking-[0.1em] uppercase text-[#8A8FA0]">${[189, 399, 699, 1199][i]}/mo</p>
                    <p className={`text-sm font-bold ${i === 1 ? "text-[#2F5FE8]" : "text-[#101319]"}`}>{t}</p>
                  </div>
                ))}
              </div>
              {[
                { label: "Website, hosted and maintained", from: 1 },
                { label: "CRM with unified inbox (SMS, email, FB/IG, Google)", from: 1 },
                { label: "Missed-call text-back", from: 1 },
                { label: "Speed-to-lead instant reply", from: 1 },
                { label: "Sales pipeline and lead tracking", from: 1 },
                { label: "Appointment calendar with reminders", from: 1 },
                { label: "Review request sending", from: 1 },
                { label: "Mobile app", from: 1 },
                { label: "Follow-up sequences managed and tuned for you", from: 2 },
                { label: "Automated review engine (request, monitor, respond)", from: 2 },
                { label: "Social post scheduling", from: 2 },
                { label: "Monthly website edits and content updates", from: 2 },
                { label: "Monthly performance report", from: 2 },
                { label: "Priority support", from: 2 },
                { label: "AI website chat (24/7 lead capture)", from: 3 },
                { label: "Database reactivation campaigns", from: 3 },
                { label: "Email and SMS campaign management", from: 3 },
                { label: "Call tracking and lead-source attribution", from: 3 },
                { label: "Quarterly SEO tune-up", from: 3 },
                { label: "Monthly strategy call", from: 3 },
                { label: "AI voice agent (after-hours and overflow calls)", from: 4 },
                { label: "Multi-location with per-location reporting", from: 4 },
                { label: "Full campaign management, all channels", from: 4 },
                { label: "Custom integrations and workflow builds", from: 4 },
                { label: "Dedicated account manager, weekly review", from: 4 },
              ].map((row, i) => (
                <div key={row.label} className={`grid grid-cols-[2fr_1fr_1fr_1fr_1fr] border-t border-[#E9E6DE] ${i % 2 ? "bg-[#FBFAF7]" : "bg-white"}`}>
                  <div className="p-3.5 text-sm font-semibold text-[#101319]">{row.label}</div>
                  {[1, 2, 3, 4].map((step) => (
                    <div key={step} className="p-3.5 flex items-center justify-center border-l border-[#E9E6DE]">
                      {step >= row.from ? (
                        <Check className="w-5 h-5 text-[#1B9E6B]" />
                      ) : (
                        <Minus className="w-4 h-4 text-[#D8D4C9]" />
                      )}
                    </div>
                  ))}
                </div>
              ))}
              <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] border-t-2 border-[#E9E6DE] bg-[#F4F2EC]">
                <div className="p-3.5 text-sm font-semibold text-[#101319]">Included monthly usage credits</div>
                {["-", "$10", "$25", "$50"].map((v, j) => (
                  <div key={j} className="p-3.5 flex items-center justify-center border-l border-[#E9E6DE]">
                    <span className="font-mono-ibm text-sm font-bold text-[#101319]">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className="text-sm text-[#5A6270] text-center mt-6">
            Frayze Reception is its own product ($197/mo) and runs alongside any plan.{" "}
            <Link to="/platform/ai-receptionist/" className="font-bold text-[#2F5FE8] hover:underline">
              See Frayze Reception
            </Link>
            {" "}· Want the detail behind each line?{" "}
            <Link to="/platform/" className="font-bold text-[#2F5FE8] hover:underline">
              Explore every capability on the platform
            </Link>
          </p>
        </div>
      </section>

      {/* ============ WHAT YOU'LL ACTUALLY PAY ============ */}
      <section className="bg-white py-16 md:py-24 border-t border-[#E9E6DE] font-instrument">
        <div className="max-w-[1312px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              {
                label: "Monthly plan",
                detail: "Fixed and public, $189 to $1,199/mo",
                desc: "The plan prices above are exactly what you're billed, every month, no games. Yearly billing takes about two months off.",
              },
              {
                label: "Required Build & Onboarding",
                detail: "Scoped to your project, fixed quote before deposit",
                desc: "One-time. Covers everything we build before you go live: site, CRM, automations, integrations. No surprises.",
              },
              {
                label: "Usage (texts, calls, AI)",
                detail: "Typically ~$15–$45/mo, billed at cost + small margin",
                desc: "Managed plans and up include monthly usage credits. Most clients spend $15–$45/mo. Think of it like a phone plan for the automation.",
                link: { href: "/usage-billing/", text: "See exactly how usage billing works" },
              },
            ].map((item, i) => (
              <div key={i} className={`reveal ${["", "reveal-delay-1", "reveal-delay-2"][i]} bg-[#F4F2EC] rounded-2xl p-6 md:p-8 border border-[#E9E6DE] lift-hover`}>
                <div className="font-mono-ibm text-[11px] tracking-[0.14em] uppercase text-[#2F5FE8] mb-2">
                  {item.label}
                </div>
                <p className="text-lg font-bold text-[#101319] mb-3">
                  {item.detail}
                </p>
                <p className="text-sm text-[#5A6270] leading-relaxed">
                  {item.desc}
                </p>
                {"link" in item && item.link && (
                  <Link to={item.link.href} className="inline-flex items-center gap-1.5 mt-4 text-sm font-bold text-[#2F5FE8] hover:text-[#2049BE] transition-colors">
                    {item.link.text}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div className="bg-[#F4F2EC] border border-[#E9E6DE] rounded-xl p-6 text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold text-[#101319]">
              The Build &amp; Onboarding number is confirmed on a call before anything is signed.
            </p>
          </div>
        </div>
      </section>

      {/* ============ ROI CALCULATOR ============ */}
      <section className="bg-[#F4F2EC] py-16 md:py-24 border-t border-[#E9E6DE] font-instrument">
        <div className="max-w-[1312px] mx-auto px-6 mb-6 text-center">
          <div className="reveal font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#2F5FE8]">
            Run your numbers
          </div>
        </div>
        <div className="reveal reveal-delay-1">
          <MissedCallCalculator />
        </div>
      </section>

      {/* ============ WHY NOT HIRE AN AGENCY ============ */}
      <section className="bg-[#FBFAF7] py-16 md:py-24 border-t border-[#E9E6DE] font-instrument">
        <div className="max-w-[1312px] mx-auto px-6">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <div className="font-mono-ibm text-xs tracking-[0.16em] uppercase text-[#2F5FE8] mb-3">
              Comparison
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#101319] mb-4">
              Why not just hire an agency?
            </h2>
            <p className="text-base md:text-lg text-[#5A6270]">
              Most agencies are built for big brands. Frayze is built for lead-driven businesses: auto dealers, trades, local service operators.
            </p>
          </div>

          <div className="reveal bg-white border border-[#E9E6DE] rounded-2xl overflow-hidden">
            {/* Header row */}
            <div className="grid grid-cols-4 bg-[#F4F2EC] border-b border-[#E9E6DE]">
              <div className="p-4 border-r border-[#E9E6DE]" />
              <div className="p-4 border-r border-[#E9E6DE] text-center">
                <p className="text-xs font-bold text-[#8A8FA0] font-mono-ibm">Freelancer</p>
              </div>
              <div className="p-4 border-r border-[#E9E6DE] text-center">
                <p className="text-xs font-bold text-[#8A8FA0] font-mono-ibm">Agency</p>
              </div>
              <div className="p-4 text-center">
                <p className="text-xs font-bold text-[#2F5FE8] font-mono-ibm">Frayze</p>
              </div>
            </div>

            {/* Rows */}
            {comparisonRows.map((row, i) => (
              <div
                key={i}
                className={`reveal ${i % 2 === 0 ? "reveal-delay-1" : ""} grid grid-cols-4 border-t border-[#E9E6DE] ${
                  i % 2 === 0 ? "bg-white" : "bg-[#FBFAF7]"
                }`}
              >
                <div className="p-4 border-r border-[#E9E6DE]">
                  <p className="text-sm font-bold text-[#101319]">{row.label}</p>
                </div>
                {[row.freelancer, row.agency, row.frayze].map((val, j) => (
                  <div
                    key={j}
                    className={`p-4 text-center flex items-center justify-center ${
                      j < 2 ? "border-r border-[#E9E6DE]" : ""
                    }`}
                  >
                    {val === true ? (
                      <Check className="w-5 h-5 text-[#1B9E6B]" />
                    ) : val === false ? (
                      <span className="text-[#C9C4B8] font-bold">–</span>
                    ) : (
                      <span
                        className={`text-sm font-medium ${
                          j === 2 ? "text-[#2F5FE8] font-bold" : "text-[#5A6270]"
                        }`}
                      >
                        {val}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRICING FAQ ============ */}
      <ServiceFAQ
        id="pricing-faq"
        title="Pricing questions, straight answers"
        description="The things people actually ask on the first call."
        items={[
          {
            question: "What's fixed and what's scoped?",
            answer: "The monthly plan prices are fixed and public, exactly what gets billed. The one scoped number is Required Build & Onboarding, because a two-person shop and a six-bay dealership aren't the same build. That quote is fixed on a call before any deposit.",
          },
          {
            question: "What is Required Build & Onboarding?",
            answer: "The one-time build fee. It covers everything we create before you go live: the site, CRM setup, automations, and integrations. Fixed quote before any deposit, no surprises after.",
          },
          {
            question: "Am I locked into a contract?",
            answer: "No. Everything is month to month. We keep clients by performing, not by paperwork.",
          },
          {
            question: "Can I move between steps?",
            answer: "Yes, any time. Most clients start at Core or Managed and move up when the pipeline demands it. Nothing gets rebuilt when you do, since every plan runs on the same system.",
          },
          {
            question: "What do the usage credits cost?",
            answer: "Texts, calls, and AI run on usage credits billed at cost plus a small margin, like a phone plan. Managed includes $10/mo in credits, Growth $25, Scale $50. Most clients spend $15 to $45 a month all-in.",
          },
          {
            question: "How fast can I be live?",
            answer: "Websites launch in 7 days. Frayze Reception can be answering your calls within days of onboarding. Managed steps roll out in phases so something is working for you in the first week.",
          },
        ]}
      />

      {/* ============ BUILT FOR LONG-TERM PARTNERSHIP ============ */}
      <section className="bg-white py-16 md:py-24 border-t border-[#E9E6DE] font-instrument">
        <div className="max-w-[1312px] mx-auto px-6">
          <div className="reveal grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E9E6DE] border border-[#E9E6DE] rounded-2xl overflow-hidden">
            {[
              {
                title: "No contracts",
                desc: "We earn your business every month.",
              },
              {
                title: "No forced upgrades",
                desc: "Move up only when you're ready.",
              },
              {
                title: "Self-serve upgrades",
                desc: "Add more users or features anytime.",
              },
              {
                title: "Built to last",
                desc: "Infrastructure you own forever.",
              },
            ].map((item, i) => (
              <div key={i} className="bg-white p-8 text-left hover:bg-[#FBFAF7] transition-colors">
                <div className="font-mono-ibm text-xs text-[#2F5FE8] font-bold mb-3">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="text-lg md:text-xl font-bold text-[#101319] mb-2">
                  {item.title}
                </h3>
                <p className="text-sm md:text-base text-[#5A6270]">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative overflow-hidden bg-[#101319] text-white py-24 md:py-32 font-instrument">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 left-1/4 w-[36rem] h-[36rem] rounded-full bg-[radial-gradient(circle,rgba(47,95,232,0.18),transparent_65%)] blur-3xl animate-orbA" />
          <div className="absolute -bottom-40 right-1/4 w-[32rem] h-[32rem] rounded-full bg-[radial-gradient(circle,rgba(19,167,184,0.10),transparent_65%)] blur-3xl animate-orbB" />
        </div>
        <div className="relative z-10 max-w-[1312px] mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            Let's architect the right system
          </h2>
          <p className="text-lg text-[#8A8FA0] mb-8 max-w-2xl mx-auto">
            Every engagement starts with a strategy session to assess volume, identify bottlenecks, and confirm the right tier.
          </p>
          <Link to="/book-consultation/" className="inline-block bg-[#2F5FE8] hover:bg-[#2049BE] text-white font-bold px-8 py-4 rounded-xl transition-all">
            Book Strategy Session
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
